import { useContext } from 'react';
import { StateContext } from '@/App';
import { useFlagCheck } from '@/utils/use-feature-flags';
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation,
} from 'react-router-dom';
import SelectAccount from './SelectAccount';
import AccountTransfer from './AccountTransfer';
import DeclareGuardian from './DeclareGuardian';
import { ActiveRelationshipAccount, Trigger } from '@/utils/types';
import trackEvent from '@/utils/amplitude';

const Accounts = () => {
  const { state } = useContext(StateContext);
  const { isAllowed } = useFlagCheck();
  const graduatingMinorsEnabled = isAllowed({ releaseGraduatingMinors: true });
  const navigate = useNavigate();
  const location = useLocation();

  const trackAccountAction = ({
    name,
    cta_type,
    metadata,
    trigger = 'Interaction',
    account,
  }: {
    name: string;
    cta_type?: string;
    metadata?: any;
    trigger?: Trigger;
    account?: ActiveRelationshipAccount;
  }) => {
    trackEvent({
      product_area: 'GraduatingMinor',
      trigger,
      name,
      metadata: {
        workflow: 'guardian',
        cta_type: cta_type || null,
        minor_patient_uuid: account?.patient_uuid,
        guardian_uuid: account?.acting_agent_uuid,
        ...metadata,
      },
    });
  };

  const handleTransferClick = (account: ActiveRelationshipAccount) => {
    if (graduatingMinorsEnabled) {
      navigate(`/account-transfer/${account.patient_uuid}`);
      trackAccountAction({
        name: 'chooseProfilePage_CTAClicked',
        cta_type: 'transfer-account',
        account,
      });
    }
  };

  const AccountActionLayout = ({ children }: { children: React.ReactNode }) => (
    <div className="h-screen w-full flex justify-center items-center bg-[url('/background-sky.svg')] bg-cover bg-center">
      <div className="w-full max-w-[920px] rounded-xl bg-white flex justify-center items-center mx-4 py-8 md:w-[64%] md:h-[578px] md:px-8">
        {children}
      </div>
    </div>
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className={state.ui.modal ? 'overflow-y-hidden h-screen' : ''}>
            <SelectAccount onTransferClick={handleTransferClick} />
          </div>
        }
      />
      <Route
        path="account-transfer/:patientUuid"
        element={
          <AccountActionLayout>
            <AccountTransfer trackAccountAction={trackAccountAction} />
          </AccountActionLayout>
        }
      />
      <Route
        path="declare-guardian/:patientUuid"
        element={
          <AccountActionLayout>
            <DeclareGuardian trackAccountAction={trackAccountAction} />
          </AccountActionLayout>
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={`/?redirect=${encodeURIComponent(location.pathname + location.search)}`}
            replace
          />
        }
      />
    </Routes>
  );
};

export default Accounts;
